import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Hash, ArrowRight, X } from "lucide-react";

/**
 * SheetJumpOverlay — type a sheet number to jump anywhere.
 *
 * Trigger: typing a digit or letter (when no input has focus) while inside
 * the SheetViewer. Behaves like a Ctrl-K command palette, but scoped to the
 * current project's sheet list and aimed at muscle-memory operators.
 *
 * Enter → navigate to /sheet/<id>. Esc → close.
 */
export default function SheetJumpOverlay({ projectId, currentSheetId, onNavigate, onClose }) {
  const [query, setQuery] = useState("");
  const [sheets, setSheets] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/sheets`).then((r) => setSheets(r.data || []));
  }, [projectId]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const matches = useMemo(() => {
    if (!query.trim()) return sheets.slice(0, 8);
    const q = query.trim().toLowerCase();
    return sheets
      .filter((s) =>
        s.sheet_number.toLowerCase().includes(q) ||
        (s.sheet_title || "").toLowerCase().includes(q),
      )
      .slice(0, 12);
  }, [sheets, query]);

  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const first = matches[0];
      if (first) onNavigate(first.id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[12vh]"
      onClick={onClose}
      data-testid="sheet-jump-overlay"
    >
      <div
        className="w-[440px] max-w-[92vw] rounded-lg border border-[var(--fop-border-hi)] bg-[var(--fop-surface)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--fop-border)]">
          <Hash size={14} className="text-[var(--fop-text-3)]" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Jump to sheet # or title…"
            className="flex-1 bg-transparent border-0 font-mono text-sm outline-none focus:outline-none focus:ring-0 px-0"
            data-testid="sheet-jump-input"
          />
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" aria-label="Close">
            <X size={13} />
          </button>
        </div>
        <ul className="max-h-[55vh] overflow-y-auto">
          {matches.length === 0 && (
            <li className="px-3 py-6 text-center text-xs text-[var(--fop-text-3)]">No matches.</li>
          )}
          {matches.map((s, idx) => (
            <li key={s.id}>
              <button
                onClick={() => onNavigate(s.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[var(--fop-surface-2)] transition-colors ${
                  s.id === currentSheetId ? "bg-[var(--fop-surface-2)]" : ""
                } ${idx === 0 ? "border-l-2 border-[var(--fop-primary)]" : ""}`}
                data-testid={`sheet-jump-row-${s.sheet_number}`}
              >
                <div className="font-mono font-bold text-sm text-[var(--fop-text)] w-24 shrink-0">
                  {s.sheet_number}
                </div>
                <div className="flex-1 min-w-0 text-xs text-[var(--fop-text-2)] truncate">
                  {s.sheet_title}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)] shrink-0">
                  REV {s.revision}
                </div>
                {idx === 0 && <ArrowRight size={12} className="text-[var(--fop-primary)] shrink-0" />}
              </button>
            </li>
          ))}
        </ul>
        <div className="px-3 py-1.5 border-t border-[var(--fop-border)] flex items-center justify-between text-[10px] font-mono text-[var(--fop-text-3)]">
          <span>
            <kbd className="px-1 py-0.5 bg-[var(--fop-surface-2)] rounded">Enter</kbd> jump
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-[var(--fop-surface-2)] rounded">Esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}
